/**
 * AppICEMAP — StatsScreen
 * Custom element: `<stats-screen>`
 *
 * Full-screen overlay with tab switcher: "Personal" / "Comunidad".
 * Integrates PersonalDashboard and CommunityDashboard.
 *
 * Events:
 *   stats:close → { }
 */

import { isAuthenticated } from "../../api/directus.js";
import {
	fetchPersonalStats,
	fetchCommunityStats,
	fetchActivityTimeline,
} from "../../api/stats.js";
// Side-effect imports: these register <personal-dashboard> and
// <community-dashboard>. Without them the tags stay inert and both tabs render
// empty, which is exactly what was happening.
import "./PersonalDashboard.js";
import "./CommunityDashboard.js";
import iconCss from "../../../css/utilities/phosphor-shadow.css?inline";

export class StatsScreen extends HTMLElement {
	constructor() {
		super();
		this.attachShadow({ mode: "open" });
		this._tab = "personal"; // 'personal' | 'community'
		this._personalStats = null;
		this._communityStats = null;
		this._activity = null;
		this._loadingPersonal = false;
		this._loadingCommunity = false;
	}

	connectedCallback() {
		this._render();
	}

	/* ── Public API ──────────────────────────────────────────────────────── */

	show() {
		this.style.display = "block";
		this._render();
		this._loadData();
	}

	hide() {
		this.style.display = "none";
	}

	/**
	 * Wait for a child custom element to be defined and upgraded.
	 * @param {HTMLElement} el
	 * @returns {Promise<void>}
	 */
	async _waitForUpgrade(el) {
		const tag = el.tagName.toLowerCase();
		if (customElements.get(tag)) return;
		await customElements.whenDefined(tag);
		// Small delay for the element to finish its connectedCallback
		await new Promise((r) => requestAnimationFrame(r));
	}

	/* ── Private ─────────────────────────────────────────────────────────── */

	_bindEvents() {
		this.shadowRoot
			.querySelector('[data-action="back"]')
			?.addEventListener("click", () => {
				this._close();
			});

		this.shadowRoot
			.querySelector('[data-tab="personal"]')
			?.addEventListener("click", () => {
				this._switchTab("personal");
			});

		this.shadowRoot
			.querySelector('[data-tab="community"]')
			?.addEventListener("click", () => {
				this._switchTab("community");
			});
	}

	async _switchTab(tab) {
		this._tab = tab;
		this._render();

		// Load data if needed (fires render again, which updates children)
		if (tab === "personal" && !this._personalStats && !this._loadingPersonal) {
			await this._loadPersonal();
		}
		if (
			tab === "community" &&
			!this._communityStats &&
			!this._loadingCommunity
		) {
			await this._loadCommunity();
		}
	}

	async _loadData() {
		// Load both in parallel
		await Promise.all([this._loadPersonal(), this._loadCommunity()]);
	}

	async _loadPersonal() {
		if (!isAuthenticated()) return;
		this._loadingPersonal = true;
		this._render();

		try {
			const [stats, activity] = await Promise.all([
				fetchPersonalStats(),
				fetchActivityTimeline(30),
			]);

			this._personalStats = stats;
			this._activity = activity;
		} catch (err) {
			console.error("[StatsScreen] Personal stats error:", err);
		} finally {
			this._loadingPersonal = false;
			this._render();
		}
	}

	async _loadCommunity() {
		this._loadingCommunity = true;
		this._render();

		try {
			this._communityStats = await fetchCommunityStats();
		} catch (err) {
			console.error("[StatsScreen] Community stats error:", err);
		} finally {
			this._loadingCommunity = false;
			this._render();
		}
	}

	_render() {
		const isPersonal = this._tab === "personal";
		const isCommunity = this._tab === "community";
		const isAuthed = isAuthenticated();

		this.shadowRoot.innerHTML = `
			<style>${iconCss}
				:host {
					display: none;
					position: fixed; inset: 0; z-index: 800;
					background: var(--color-bg, #161826);
					overflow-y: auto;
					-webkit-overflow-scrolling: touch;
				}

				.ss { display: flex; flex-direction: column; min-height: 100%; }

				/* Header */
				.ss-header {
					display: flex; align-items: center; gap: var(--space-3, 12px);
					padding: var(--space-4, 16px);
					padding-top: max(var(--space-4, 16px), env(safe-area-inset-top));
					border-bottom: 1px solid var(--color-divider);
					position: sticky; top: 0; z-index: 10;
					background: var(--color-bg, #161826);
				}
				.ss-header__back {
					width: 36px; height: 36px;
					display: flex; align-items: center; justify-content: center;
					border: none; background: none;
					color: var(--color-text, #e9e9ed);
					font-size: 1.25rem; cursor: pointer;
					border-radius: var(--radius-full, 9999px);
					transition: background var(--duration-fast, 150ms);
				}
				.ss-header__back:hover { background: var(--color-neutral-800, #3f424d); }
				.ss-header h2 {
					font-size: var(--text-lg, 1.25rem);
					font-weight: var(--font-heading-weight, 500);
					color: var(--color-text, #e9e9ed);
					margin: 0; flex: 1;
				}

				/* Tabs */
				.ss-tabs {
					display: flex; gap: var(--space-1, 4px);
					padding: var(--space-3, 12px) var(--space-4, 16px);
					background: var(--color-bg, #161826);
					position: sticky; top: 60px; z-index: 9;
					border-bottom: 1px solid var(--color-divider);
				}
				.ss-tab {
					flex: 1; padding: var(--space-2, 8px) var(--space-4, 16px);
					border: none; border-radius: var(--radius-full, 9999px);
					background: transparent;
					color: var(--color-neutral-400, #b2b6ca);
					font-size: var(--text-sm, 0.8125rem);
					font-weight: 500; cursor: pointer;
					transition: all var(--duration-fast, 150ms);
				}
				.ss-tab--active {
					background: var(--color-accent, #9184d9);
					color: #fff;
				}
				.ss-tab:not(.ss-tab--active):hover {
					background: var(--color-neutral-800, #3f424d);
				}

				/* Content */
				.ss-content {
					flex: 1; padding: var(--space-4, 16px);
					padding-bottom: max(var(--space-4, 16px), env(safe-area-inset-bottom));
				}

				/* Guest notice */
				.ss-guest {
					text-align: center; padding: var(--space-12, 48px) var(--space-4, 16px);
					color: var(--color-neutral-400, #b2b6ca);
				}
				.ss-guest i { font-size: 2.5rem; display: block; margin-bottom: var(--space-4, 16px); }
				.ss-guest p { font-size: var(--text-base, 0.9375rem); margin: 0; }

				/* Error state */
				.ss-error {
					text-align: center; padding: var(--space-8, 32px) var(--space-4, 16px);
					color: var(--color-neutral-400, #b2b6ca);
				}
				.ss-error i { font-size: 2rem; display: block; margin-bottom: var(--space-2, 8px); }
				.ss-error__retry {
					margin-top: var(--space-3, 12px);
					padding: var(--space-2, 8px) var(--space-5, 20px);
					border: none; border-radius: var(--radius-full, 9999px);
					background: var(--color-accent, #9184d9);
					color: #fff; font-size: var(--text-sm, 0.8125rem);
					font-weight: 500; cursor: pointer;
				}
			</style>

			<div class="ss">
				<header class="ss-header">
					<button class="ss-header__back" data-action="back" aria-label="Cerrar estadísticas">
						<i class="ph ph-x"></i>
					</button>
					<h2>Estadísticas</h2>
				</header>

				<div class="ss-tabs">
					<button class="ss-tab ${isPersonal ? "ss-tab--active" : ""}" data-tab="personal"
						${!isAuthed ? 'disabled style="opacity:0.4"' : ""}>
						Personal
					</button>
					<button class="ss-tab ${isCommunity ? "ss-tab--active" : ""}" data-tab="community">
						Comunidad
					</button>
				</div>

				<div class="ss-content">
					${isPersonal ? this._renderPersonalTab() : this._renderCommunityTab()}
				</div>
			</div>
		`;

		// Re-bind events on the new DOM
		this._bindEvents();
		// Pass data to child components once they appear
		requestAnimationFrame(() => this._updateChildComponents());
	}

	_renderPersonalTab() {
		if (!isAuthenticated()) {
			return `
				<div class="ss-guest">
					<i class="ph ph-user-circle"></i>
					<p>Inicia sesión para ver tus estadísticas personales</p>
				</div>
			`;
		}

		return `
			<personal-dashboard
				${this._loadingPersonal ? "loading" : ""}
			></personal-dashboard>
		`;
	}

	_renderCommunityTab() {
		return `
			<community-dashboard
				${this._loadingCommunity ? "loading" : ""}
			></community-dashboard>
		`;
	}

	/* Called after render to pass data to child components */

	_updateChildComponents() {
		const personalDash = this.shadowRoot.querySelector("personal-dashboard");
		if (personalDash) {
			if (this._loadingPersonal) {
				personalDash.setAttribute("loading", "");
			} else {
				personalDash.removeAttribute("loading");
			}
			personalDash.stats = this._personalStats;
			personalDash.activity = this._activity;
		}

		const communityDash = this.shadowRoot.querySelector("community-dashboard");
		if (communityDash) {
			if (this._loadingCommunity) {
				communityDash.setAttribute("loading", "");
			} else {
				communityDash.removeAttribute("loading");
			}
			communityDash.stats = this._communityStats;
		}
	}

	_close() {
		this.hide();
		this.dispatchEvent(
			new CustomEvent("stats:close", { bubbles: true, composed: true }),
		);
	}
}

if (!customElements.get("stats-screen")) {
	customElements.define("stats-screen", StatsScreen);
}
